import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ReceiptDialog } from "../components/receipts/ReceiptDialog";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { formatCurrency } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import { useCurrency } from "../contexts/CurrencyContext";
import toast from "react-hot-toast";
import axios from "axios";
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  DollarSign,
  Search,
  Package,
  User,
  Calendar,
  Coins,
  Gift,
  MessageCircle,
  Camera,
  ScanLine,
  TrendingUp,
  FileText,
  Clock,
  X,
  Printer,
  Share2,
  Timer,
  Users,
  Ticket,
  Check,
  Loader2,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  History,
} from "lucide-react";

const API = `${window.location.origin}/api`;

// رسائل تحفيزية للموظف بعد كل عملية بيع
const MOTIVATIONAL_MESSAGES = [
  "أحسنت! 🎉 استمر بهذا الأداء الرائع",
  "ممتاز! 💪 أنت نجم المبيعات",
  "رائع! ⭐ عملية ناجحة أخرى",
  "بارك الله فيك! 🌟 شغل متقن",
  "هكذا يكون العمل! 🏆 مبروك",
  "تمام التمام! ✨ الله يعطيك العافية",
  "شكراً لجهودك! 💚 أنت مميز",
  "يا بطل! 🥇 بيعة موفقة",
  "ما شاء الله! 🔥 أداء ممتاز",
  "برافو عليك! 👏 هيا للبيعة القادمة",
  "الله يوفقك! 🚀 إلى الأمام",
  "أنت الأفضل! 💎 استمر",
  "عمل رائع! 🌈 يومك مبارك",
  "تسلم إيدك! 🤝 بيعة مباركة",
  "هيا نكمل! 💫 الحماس موجود",
];

// دالة لاختيار رسالة عشوائية
const getRandomMotivationalMessage = () => {
  const randomIndex = Math.floor(Math.random() * MOTIVATIONAL_MESSAGES.length);
  return MOTIVATIONAL_MESSAGES[randomIndex];
};

export default function POSPage() {
  const { } = useAuth();
  const navigate = useNavigate();
  const { rate: usdRate } = useCurrency();
  const language = "ar";
  const { t } = useTranslation(language);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  // ✨ إدخال يدوي للعميل (نص حر بدلاً من اختيار من القائمة)
  const [customerInputMode, setCustomerInputMode] = useState("select"); // select | manual
  const [manualCustomerName, setManualCustomerName] = useState("");
  const [manualCustomerPhone, setManualCustomerPhone] = useState("");
  // ✨ نوع الدفع الآجل (آجل بفترة / أقساط / جزئي)
  const [creditMode, setCreditMode] = useState("credit_period"); // credit_period | installment | partial
  const [installmentsCount, setInstallmentsCount] = useState(3);
  const [installmentPeriodDays, setInstallmentPeriodDays] = useState(30);
  // ✨ مستوى حماية الفاتورة
  const [protectionLevel, setProtectionLevel] = useState("medium"); // weak | medium | strong
  // ✨ ملاحظات على الفاتورة
  const [saleNotes, setSaleNotes] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [activeMultiplier, setActiveMultiplier] = useState({ multiplier: 1, active_offer: null });
  const [autoWhatsApp, setAutoWhatsApp] = useState(true); // إرسال تلقائي للواتساب
  const [isScannerOpen, setIsScannerOpen] = useState(false); // حالة ماسح الباركود
  const [barcodeBuffer, setBarcodeBuffer] = useState(""); // لقراءة باركود USB
  const [lastKeyTime, setLastKeyTime] = useState(0);
  const [scannerMode, setScannerMode] = useState(false); // وضع الماسح المتواصل
  const [lastScanTime, setLastScanTime] = useState(0); // وقت آخر مسح
  const [scanHistory, setScanHistory] = useState([]); // تاريخ المسح
  const [lastScannedProduct, setLastScannedProduct] = useState(null); // آخر منتج ممسوح
  const [scanSound, setScanSound] = useState(true); // صوت المسح
  const [discount, setDiscount] = useState({ type: "none", value: 0 }); // نظام الخصومات
  const [couponCode, setCouponCode] = useState(""); // كود الكوبون
  const [appliedCoupon, setAppliedCoupon] = useState(null); // الكوبون المطبق
  const [couponLoading, setCouponLoading] = useState(false); // حالة التحقق من الكوبون
  const [cashReceived, setCashReceived] = useState(""); // المبلغ المستلم لحساب الباقي
  const [showShortcuts, setShowShortcuts] = useState(false); // عرض اختصارات لوحة المفاتيح
  const [topProducts, setTopProducts] = useState([]); // المنتجات الأكثر مبيعاً
  const [todayStats, setTodayStats] = useState(null); // إحصائيات اليوم
  const [showEndOfDayReport, setShowEndOfDayReport] = useState(false); // تقرير نهاية اليوم
  const [showShiftReport, setShowShiftReport] = useState(false); // تقرير الوردية
  const [shiftReport, setShiftReport] = useState(null); // بيانات تقرير الوردية
  const [shiftHours, setShiftHours] = useState(8); // ساعات الوردية
  
  // Receipt dialog state
  const [completedSale, setCompletedSale] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);

  // ✨ تصفّح الفواتير السابقة من رأس POS
  const [salesHistory, setSalesHistory] = useState([]);
  const [viewedSale, setViewedSale] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const ensureSalesLoaded = async () => {
    if (salesHistory.length > 0) return salesHistory;
    try {
      const res = await axios.get(`${API}/sales`);
      const list = Array.isArray(res.data) ? res.data : [];
      setSalesHistory(list);
      return list;
    } catch (e) {
      toast.error("تعذّر تحميل الفواتير");
      return [];
    }
  };

  const openInvoiceAt = async (idx) => {
    const list = await ensureSalesLoaded();
    if (list.length === 0) {
      toast.error("لا توجد فواتير سابقة");
      return;
    }
    const safeIdx = Math.max(0, Math.min(idx, list.length - 1));
    setViewedSale(list[safeIdx]);
    setIsViewerOpen(true);
  };

  const navigateInvoice = async (direction) => {
    const list = await ensureSalesLoaded();
    if (list.length === 0) {
      toast.error("لا توجد فواتير سابقة");
      return;
    }
    let idx = 0;
    if (viewedSale) {
      const cur = list.findIndex((s) => s.id === viewedSale.id);
      idx = cur < 0 ? 0 : cur;
    }
    if (direction === "first") idx = 0;
    else if (direction === "last") idx = list.length - 1;
    else if (direction === "prev") idx = Math.max(0, idx - 1);
    else if (direction === "next") idx = Math.min(list.length - 1, idx + 1);
    setViewedSale(list[idx]);
    setIsViewerOpen(true);
  };

  // Held invoices modal (داخل POS بدون مغادرة الصفحة)
  const [showHeldInvoicesModal, setShowHeldInvoicesModal] = useState(false);
  const [heldInvoicesList, setHeldInvoicesList] = useState([]);
  const [heldInvoicesLoading, setHeldInvoicesLoading] = useState(false);

  const fetchHeldInvoices = async () => {
    setHeldInvoicesLoading(true);
    try {
      const res = await axios.get(`${API}/held-invoices`);
      // الـ API يرجع {held: [...], count: N}
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.held)
          ? res.data.held
          : [];
      setHeldInvoicesList(list);
    } catch (e) {
      console.error("Held invoices fetch failed:", e);
      setHeldInvoicesList([]);
    } finally {
      setHeldInvoicesLoading(false);
    }
  };

  const openHeldInvoicesModal = () => {
    fetchHeldInvoices();
    setShowHeldInvoicesModal(true);
  };

  const resumeHeldInvoice = async (held) => {
    if (!held || !Array.isArray(held.items) || held.items.length === 0) {
      toast.error("الفاتورة المعلقة فارغة");
      return;
    }
    const restored = held.items.map((it) => ({
      product_id: it.product_id || it.id,
      product_name: it.product_name || it.name,
      name: it.product_name || it.name,
      quantity: it.quantity || 1,
      price: it.price ?? it.selling_price ?? 0,
      selling_price: it.selling_price ?? it.price ?? 0,
      purchase_price: it.purchase_price || 0,
      max_quantity: it.max_quantity || it.quantity || 999,
    }));
    setCart(restored);
    if (held.customer_id) setSelectedCustomer(held.customer_id);
    if (held.payment_type) setPaymentType(held.payment_type);
    // حذف من السيرفر
    try {
      await axios.delete(`${API}/held-invoices/${held.id}`);
    } catch {}
    toast.success("تم استكمال الفاتورة المعلقة ✓");
    setShowHeldInvoicesModal(false);
  };

  const deleteHeldInvoice = async (id) => {
    if (!window.confirm("حذف هذه الفاتورة المعلقة؟")) return;
    try {
      await axios.delete(`${API}/held-invoices/${id}`);
      setHeldInvoicesList((prev) => prev.filter((h) => h.id !== id));
      toast.success("تم الحذف");
    } catch {
      toast.error("فشل الحذف");
    }
  };

  useEffect(() => {
    fetchData();
    fetchActiveMultiplier();
    fetchTopProducts();
    fetchTodayStats();
    
    // استعادة فاتورة معلقة إن وُجدت
    try {
      const heldRaw = sessionStorage.getItem("resume_held_invoice");
      if (heldRaw) {
        const held = JSON.parse(heldRaw);
        if (held && Array.isArray(held.items) && held.items.length > 0) {
          const restored = held.items.map((it) => ({
            id: it.product_id || it.id,
            name: it.name,
            quantity: it.quantity || 1,
            price: it.price,
            selling_price: it.price,
            purchase_price: it.purchase_price,
          }));
          setCart(restored);
          if (held.customer_id) setSelectedCustomer(held.customer_id);
          // حذف الفاتورة المعلقة من السيرفر + sessionStorage
          const token = localStorage.getItem("token");
          if (held.id && token) {
            axios.delete(`${API}/held-invoices/${held.id}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => {});
          }
          sessionStorage.removeItem("resume_held_invoice");
          toast.success("تم استكمال الفاتورة المعلقة ✓");
        }
      }
    } catch (e) {
      console.warn("Resume held invoice failed:", e);
    }
  }, []);

  // جلب إحصائيات اليوم
  const fetchTodayStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/financial-summary`);
      setTodayStats(response.data.today || null);
    } catch (error) {
      console.error("Error fetching today stats:", error);
    }
  };

  // 🖨️ طباعة تقرير نهاية اليوم
  const printEndOfDayReport = () => {
    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    const dateStr = new Date().toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US", { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    
    const printContent = `
      <!DOCTYPE html>
      <html dir="${language === 'ar' ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <title>${language === "ar" ? "تقرير نهاية اليوم" : "End of Day Report"}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Arial, sans-serif; 
            padding: 20px; 
            max-width: 400px; 
            margin: 0 auto;
            direction: ${language === 'ar' ? 'rtl' : 'ltr'};
          }
          .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px dashed #333; }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .header .shop-name { font-size: 18px; color: #10b981; font-weight: bold; }
          .header .date { font-size: 14px; color: #666; margin-top: 10px; }
          .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
          .stat-box { border: 1px solid #ddd; border-radius: 8px; padding: 15px; text-align: center; }
          .stat-box.sales { border-color: #10b981; background: #f0fdf4; }
          .stat-box.transactions { border-color: #3b82f6; background: #eff6ff; }
          .stat-box.profit { border-color: #8b5cf6; background: #faf5ff; }
          .stat-box.expenses { border-color: #f59e0b; background: #fffbeb; }
          .stat-value { font-size: 22px; font-weight: bold; }
          .stat-label { font-size: 12px; color: #666; margin-top: 5px; }
          .summary { border: 1px solid #ddd; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
          .summary-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
          .summary-row:last-child { border-bottom: none; font-weight: bold; font-size: 16px; }
          .summary-row.total { border-top: 2px solid #333; padding-top: 15px; margin-top: 10px; }
          .positive { color: #10b981; }
          .negative { color: #ef4444; }
          .footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 2px dashed #333; font-size: 12px; color: #666; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="shop-name">${shopInfo?.name || 'Nazario-Hesabaty'}</div>
          <h1>📊 ${language === "ar" ? "تقرير نهاية اليوم" : "End of Day Report"}</h1>
          <div class="date">📅 ${dateStr}</div>
        </div>
        
        <div class="stats-grid">
          <div class="stat-box sales">
            <div class="stat-value positive">${formatNum(todayStats?.sales || 0)}</div>
            <div class="stat-label">${language === "ar" ? "إجمالي المبيعات (د.ع)" : "Total Sales (د.ع)"}</div>
          </div>
          <div class="stat-box transactions">
            <div class="stat-value" style="color: #3b82f6;">${todayStats?.transactions || 0}</div>
            <div class="stat-label">${language === "ar" ? "عدد المعاملات" : "Transactions"}</div>
          </div>
          <div class="stat-box profit">
            <div class="stat-value" style="color: #8b5cf6;">${formatNum(todayStats?.profit || 0)}</div>
            <div class="stat-label">${language === "ar" ? "صافي الربح (د.ع)" : "Net Profit (د.ع)"}</div>
          </div>
          <div class="stat-box expenses">
            <div class="stat-value" style="color: #f59e0b;">${formatNum(todayStats?.expenses || 0)}</div>
            <div class="stat-label">${language === "ar" ? "المصروفات (د.ع)" : "Expenses (د.ع)"}</div>
          </div>
        </div>
        
        <div class="summary">
          <div class="summary-row">
            <span>${language === "ar" ? "المبيعات:" : "Sales:"}</span>
            <span class="positive">${formatNum(todayStats?.sales || 0)} د.ع</span>
          </div>
          <div class="summary-row">
            <span>${language === "ar" ? "المشتريات:" : "Purchases:"}</span>
            <span class="negative">-${formatNum(todayStats?.purchases || 0)} د.ع</span>
          </div>
          <div class="summary-row">
            <span>${language === "ar" ? "المصروفات:" : "Expenses:"}</span>
            <span class="negative">-${formatNum(todayStats?.expenses || 0)} د.ع</span>
          </div>
          <div class="summary-row total">
            <span>${language === "ar" ? "صافي الربح:" : "Net Profit:"}</span>
            <span class="${(todayStats?.profit || 0) >= 0 ? 'positive' : 'negative'}">${formatNum(todayStats?.profit || 0)} د.ع</span>
          </div>
        </div>
        
        <div class="footer">
          <p>🕐 ${language === "ar" ? "وقت الطباعة:" : "Printed at:"} ${new Date().toLocaleTimeString(language === "ar" ? "ar-IQ" : "en-US")}</p>
          <p style="margin-top: 10px;">${language === "ar" ? "شكراً لاستخدامكم نازاريو حساباتي" : "Thank you for using Nazario-Hesabaty"}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=450,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
    
    toast.success(language === "ar" ? "جاري الطباعة..." : "Printing...");
  };

  // 📤 مشاركة تقرير نهاية اليوم عبر WhatsApp
  const shareEndOfDayReport = () => {
    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    const dateStr = new Date().toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US", { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    
    let message = language === "ar" 
      ? `📊 *تقرير نهاية اليوم*\n`
      : `📊 *End of Day Report*\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += `📅 ${dateStr}\n`;
    message += `━━━━━━━━━━━━━━━\n\n`;
    
    message += language === "ar" 
      ? `💰 *إجمالي المبيعات:* ${formatNum(todayStats?.sales || 0)} د.ع\n`
      : `💰 *Total Sales:* ${formatNum(todayStats?.sales || 0)} د.ع\n`;
    
    message += language === "ar"
      ? `🔢 *عدد المعاملات:* ${todayStats?.transactions || 0}\n`
      : `🔢 *Transactions:* ${todayStats?.transactions || 0}\n`;
    
    message += language === "ar"
      ? `📈 *صافي الربح:* ${formatNum(todayStats?.profit || 0)} د.ع\n`
      : `📈 *Net Profit:* ${formatNum(todayStats?.profit || 0)} د.ع\n`;
    
    message += language === "ar"
      ? `💸 *المصروفات:* ${formatNum(todayStats?.expenses || 0)} د.ع\n`
      : `💸 *Expenses:* ${formatNum(todayStats?.expenses || 0)} د.ع\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += language === "ar" 
      ? `🕐 ${new Date().toLocaleTimeString("ar-IQ")}`
      : `🕐 ${new Date().toLocaleTimeString()}`;
    
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success(language === "ar" ? "جاري فتح WhatsApp..." : "Opening WhatsApp...");
  };

  // 📋 جلب تقرير الوردية
  const fetchShiftReport = async () => {
    try {
      const response = await axios.get(`${API}/employees/shift-report?hours=${shiftHours}`);
      setShiftReport(response.data);
      setShowShiftReport(true);
    } catch (error) {
      console.error("Error fetching shift report:", error);
      toast.error(language === "ar" ? "خطأ في جلب تقرير الوردية" : "Error fetching shift report");
    }
  };

  // 🖨️ طباعة تقرير الوردية
  const printShiftReport = () => {
    if (!shiftReport) return;
    
    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    const now = new Date();
    
    const printContent = `
      <!DOCTYPE html>
      <html dir="${language === 'ar' ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <title>${language === "ar" ? "تقرير الوردية" : "Shift Report"}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Arial, sans-serif; 
            padding: 20px; 
            max-width: 400px; 
            margin: 0 auto;
            direction: ${language === 'ar' ? 'rtl' : 'ltr'};
          }
          .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px dashed #333; }
          .header h1 { font-size: 22px; margin-bottom: 5px; }
          .header .shop-name { font-size: 18px; color: #8b5cf6; font-weight: bold; }
          .header .employee { font-size: 16px; color: #10b981; margin-top: 8px; }
          .header .time-range { font-size: 12px; color: #666; margin-top: 5px; }
          .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
          .stat-box { border: 1px solid #ddd; border-radius: 8px; padding: 12px; text-align: center; }
          .stat-box.total { border-color: #10b981; background: #f0fdf4; }
          .stat-box.cash { border-color: #3b82f6; background: #eff6ff; }
          .stat-box.credit { border-color: #f59e0b; background: #fffbeb; }
          .stat-box.count { border-color: #8b5cf6; background: #faf5ff; }
          .stat-value { font-size: 18px; font-weight: bold; }
          .stat-label { font-size: 11px; color: #666; margin-top: 3px; }
          .products { border: 1px solid #ddd; border-radius: 8px; padding: 12px; margin-bottom: 15px; }
          .products h3 { font-size: 14px; margin-bottom: 10px; color: #333; }
          .product-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee; font-size: 12px; }
          .product-row:last-child { border-bottom: none; }
          .footer { text-align: center; margin-top: 15px; padding-top: 15px; border-top: 2px dashed #333; font-size: 11px; color: #666; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="shop-name">${shopInfo?.name || 'Nazario-Hesabaty'}</div>
          <h1>⏱️ ${language === "ar" ? "تقرير الوردية" : "Shift Report"}</h1>
          <div class="employee">👤 ${shiftReport.employee_name}</div>
          <div class="time-range">🕐 ${language === "ar" ? `${shiftHours} ساعات` : `${shiftHours} hours`} | ${now.toLocaleTimeString(language === "ar" ? "ar-IQ" : "en-US")}</div>
        </div>
        
        <div class="stats-grid">
          <div class="stat-box total">
            <div class="stat-value" style="color: #10b981;">${formatNum(shiftReport.total_sales)}</div>
            <div class="stat-label">${language === "ar" ? "إجمالي المبيعات (د.ع)" : "Total Sales (د.ع)"}</div>
          </div>
          <div class="stat-box count">
            <div class="stat-value" style="color: #8b5cf6;">${shiftReport.transaction_count}</div>
            <div class="stat-label">${language === "ar" ? "عدد المعاملات" : "Transactions"}</div>
          </div>
          <div class="stat-box cash">
            <div class="stat-value" style="color: #3b82f6;">${formatNum(shiftReport.cash_sales)}</div>
            <div class="stat-label">${language === "ar" ? "مبيعات نقدية (د.ع)" : "Cash Sales (د.ع)"}</div>
          </div>
          <div class="stat-box credit">
            <div class="stat-value" style="color: #f59e0b;">${formatNum(shiftReport.credit_sales)}</div>
            <div class="stat-label">${language === "ar" ? "مبيعات آجلة (د.ع)" : "Credit Sales (د.ع)"}</div>
          </div>
        </div>
        
        ${shiftReport.top_products?.length > 0 ? `
        <div class="products">
          <h3>📦 ${language === "ar" ? "المنتجات المباعة" : "Products Sold"}</h3>
          ${shiftReport.top_products.map(p => `
            <div class="product-row">
              <span>${p.name} (${p.quantity})</span>
              <span>${formatNum(p.total)} د.ع</span>
            </div>
          `).join('')}
        </div>
        ` : ''}
        
        <div class="footer">
          <p>📅 ${now.toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p style="margin-top: 5px;">${language === "ar" ? "شكراً لعملك الجاد!" : "Thank you for your hard work!"}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=450,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
    
    toast.success(language === "ar" ? "جاري الطباعة..." : "Printing...");
  };

  // 📤 مشاركة تقرير الوردية عبر WhatsApp
  const shareShiftReport = () => {
    if (!shiftReport) return;
    
    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    const now = new Date();
    
    let message = language === "ar" 
      ? `⏱️ *تقرير الوردية*\n`
      : `⏱️ *Shift Report*\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += `👤 ${shiftReport.employee_name}\n`;
    message += `🕐 ${language === "ar" ? `${shiftHours} ساعات` : `${shiftHours} hours`}\n`;
    message += `━━━━━━━━━━━━━━━\n\n`;
    
    message += language === "ar" 
      ? `💰 *إجمالي المبيعات:* ${formatNum(shiftReport.total_sales)} د.ع\n`
      : `💰 *Total Sales:* ${formatNum(shiftReport.total_sales)} د.ع\n`;
    
    message += language === "ar"
      ? `🔢 *عدد المعاملات:* ${shiftReport.transaction_count}\n`
      : `🔢 *Transactions:* ${shiftReport.transaction_count}\n`;
    
    message += language === "ar"
      ? `💵 *نقدي:* ${formatNum(shiftReport.cash_sales)} د.ع\n`
      : `💵 *Cash:* ${formatNum(shiftReport.cash_sales)} د.ع\n`;
    
    message += language === "ar"
      ? `📝 *آجل:* ${formatNum(shiftReport.credit_sales)} د.ع\n`
      : `📝 *Credit:* ${formatNum(shiftReport.credit_sales)} د.ع\n`;
    
    message += language === "ar"
      ? `📦 *المنتجات المباعة:* ${shiftReport.items_sold} قطعة\n`
      : `📦 *Items Sold:* ${shiftReport.items_sold} items\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += `📅 ${now.toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US")}\n`;
    message += `🕐 ${now.toLocaleTimeString(language === "ar" ? "ar-IQ" : "en-US")}`;
    
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success(language === "ar" ? "جاري فتح WhatsApp..." : "Opening WhatsApp...");
  };

  // جلب المنتجات الأكثر مبيعاً
  const fetchTopProducts = async () => {
    try {
      const response = await axios.get(`${API}/analytics/top-products?limit=5&period=week`);
      setTopProducts(response.data.top_products || []);
    } catch (error) {
      console.error("Error fetching top products:", error);
    }
  };

  // 🔊 تشغيل صوت عند المسح الناجح
  const playBeepSound = (success = true) => {
    if (!scanSound) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (success) {
        // صوت نجاح - نغمة عالية قصيرة
        oscillator.frequency.value = 1200;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
      } else {
        // صوت خطأ - نغمة منخفضة
        oscillator.frequency.value = 300;
        oscillator.type = 'square';
        gainNode.gain.value = 0.2;
      }
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) {
      console.log("Audio not supported");
    }
  };

  // ⌨️ اختصارات لوحة المفاتيح
  useEffect(() => {
    const handleShortcuts = (e) => {
      // تجاهل إذا كان المؤشر في حقل إدخال
      const activeElement = document.activeElement;
      const isInputFocused = activeElement.tagName === "INPUT" || 
                            activeElement.tagName === "TEXTAREA";
      
      if (e.key === "F1") {
        e.preventDefault();
        setShowShortcuts(!showShortcuts);
      }
      
      if (isInputFocused) return;
      
      // F2 - فتح ماسح الباركود
      if (e.key === "F2") {
        e.preventDefault();
        setIsScannerOpen(true);
      }
      
      // F3 - التركيز على حقل البحث
      if (e.key === "F3") {
        e.preventDefault();
        document.querySelector('[data-testid="pos-search-input"]')?.focus();
      }
      
      // F4 - إتمام البيع
      if (e.key === "F4" && cart.length > 0) {
        e.preventDefault();
        handleCheckout();
      }
      
      // F5 - تبديل نوع الدفع
      if (e.key === "F5") {
        e.preventDefault();
        setPaymentType(paymentType === "cash" ? "credit" : "cash");
      }
      
      // Escape - إلغاء وضع الماسح أو مسح السلة
      if (e.key === "Escape") {
        e.preventDefault();
        if (scannerMode) {
          setScannerMode(false);
          toast.success(language === "ar" ? "تم إلغاء وضع الماسح" : "Scanner mode cancelled");
        } else if (isScannerOpen) {
          setIsScannerOpen(false);
        } else if (cart.length > 0) {
          if (window.confirm(language === "ar" ? "هل تريد مسح السلة؟" : "Clear cart?")) {
            setCart([]);
            setDiscount({ type: "none", value: 0 });
            setCashReceived("");
          }
        }
      }
      
      // F6 - تفعيل وضع الماسح USB
      if (e.key === "F6") {
        e.preventDefault();
        setScannerMode(!scannerMode);
        toast.success(
          !scannerMode 
            ? (language === "ar" ? "🟢 وضع الماسح USB مفعّل" : "🟢 USB Scanner Mode enabled")
            : (language === "ar" ? "تم إلغاء وضع الماسح" : "Scanner mode disabled")
        );
      }
    };
    
    window.addEventListener("keydown", handleShortcuts);
    return () => window.removeEventListener("keydown", handleShortcuts);
  }, [cart, paymentType, isScannerOpen, showShortcuts, scannerMode]);

  // 🔥 الاستماع لقارئ الباركود USB - نظام محسّن
  useEffect(() => {
    let buffer = "";
    let lastTime = Date.now();
    let scannerTimeout = null;
    
    const processBarcode = (barcode) => {
      const now = Date.now();
      // منع المسح المتكرر خلال 500ms
      if (now - lastScanTime < 500) {
        return;
      }
      setLastScanTime(now);
      handleBarcodeScan(barcode);
    };
    
    const handleKeyDown = (e) => {
      const currentTime = Date.now();
      const activeElement = document.activeElement;
      const isInputFocused = activeElement.tagName === "INPUT" || 
                            activeElement.tagName === "TEXTAREA" ||
                            activeElement.isContentEditable;
      
      // تجاهل مفاتيح التحكم
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      
      // === وضع الماسح المتواصل ===
      if (scannerMode) {
        // في وضع الماسح، نستمع لكل شيء
        if (e.key === "Escape") {
          setScannerMode(false);
          buffer = "";
          return;
        }
        
        // مسح المخزن المؤقت إذا مر وقت طويل (أكثر من 100ms = كتابة يدوية)
        if (currentTime - lastTime > 100) {
          buffer = "";
        }
        lastTime = currentTime;
        
        // عند Enter، معالجة الباركود
        if (e.key === "Enter" && buffer.length >= 3) {
          e.preventDefault();
          e.stopPropagation();
          processBarcode(buffer.trim());
          buffer = "";
          return;
        }
        
        // إضافة الأحرف للمخزن المؤقت
        if (e.key.length === 1 && /[a-zA-Z0-9\-_.]/.test(e.key)) {
          e.preventDefault();
          buffer += e.key;
        }
        return;
      }
      
      // === الوضع العادي ===
      
      // إذا المؤشر في حقل البحث وضغط Enter
      if (isInputFocused && activeElement.dataset.testid === "pos-search-input" && e.key === "Enter") {
        const value = activeElement.value.trim();
        if (value.length >= 3) {
          const product = products.find(p => 
            p.barcode === value || 
            p.code === value ||
            p.barcode?.toLowerCase() === value.toLowerCase()
          );
          if (product) {
            e.preventDefault();
            processBarcode(value);
            setSearchTerm("");
            activeElement.blur(); // إزالة التركيز بعد المسح
            return;
          }
        }
      }
      
      // استماع للماسح USB في الخلفية
      if (!isInputFocused) {
        // مسح المخزن المؤقت إذا مر وقت طويل
        if (currentTime - lastTime > 100) {
          buffer = "";
        }
        lastTime = currentTime;
        
        // عند Enter، معالجة الباركود
        if (e.key === "Enter" && buffer.length >= 3) {
          e.preventDefault();
          processBarcode(buffer.trim());
          buffer = "";
          return;
        }
        
        // إضافة الأحرف للمخزن المؤقت
        if (e.key.length === 1 && /[a-zA-Z0-9\-_.]/.test(e.key)) {
          buffer += e.key;
          
          // إعادة تعيين المخزن بعد 2 ثانية من عدم النشاط
          if (scannerTimeout) clearTimeout(scannerTimeout);
          scannerTimeout = setTimeout(() => {
            buffer = "";
          }, 2000);
        }
      }
    };
    
    window.addEventListener("keydown", handleKeyDown, true); // استخدام capture
    
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      if (scannerTimeout) clearTimeout(scannerTimeout);
    };
  }, [products, scannerMode, lastScanTime]);

  const fetchData = async () => {
    try {
      const [productsRes, customersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/settings`),
      ]);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
      setShopInfo({
        name: settingsRes.data.shop_name,
        phone: settingsRes.data.phone,
        whatsapp: settingsRes.data.whatsapp_number,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchActiveMultiplier = async () => {
    try {
      const response = await axios.get(`${API}/offers/active-multiplier`);
      setActiveMultiplier(response.data);
    } catch (error) {
      console.error("Error fetching multiplier:", error);
    }
  };

  // صوت عند إضافة منتج للسلة
  const playAddSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.2;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 100);
    } catch (e) {
      // تجاهل إذا الصوت غير مدعوم
    }
  };

  const addToCart = (product) => {
    // ✨ نسمح بالبيع حتى لو الكمية = 0 أو سالبة، لكن نعرض تحذير
    if (product.quantity <= 0) {
      toast(
        (t) => (
          <span>
            ⚠️ {language === "ar"
              ? `المنتج "${product.name}" نافذ من المخزون - تم الإضافة على مسؤوليتك`
              : `"${product.name}" is out of stock - added at your discretion`}
          </span>
        ),
        { duration: 4500, icon: "⚠️", style: { background: "#7c2d12", color: "#fff" } }
      );
    }

    // تشغيل الصوت
    playAddSound();

    const existingItem = cart.find((item) => item.product_id === product.id);
    if (existingItem) {
      if (product.quantity > 0 && existingItem.quantity >= product.quantity) {
        toast(
          (t) => (
            <span>
              ⚠️ {language === "ar"
                ? `تجاوزت الكمية المتوفرة (${product.quantity}) - سيظهر مخزون سالب`
                : `Exceeded available stock (${product.quantity}) - stock will go negative`}
            </span>
          ),
          { duration: 4000, icon: "⚠️", style: { background: "#92400e", color: "#fff" } }
        );
      }
      setCart(
        cart.map((item) =>
          item.product_id === product.id
            ? {
                ...item,
                quantity: item.quantity + 1,
                total: (item.quantity + 1) * item.price,
              }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          price: product.sale_price,
          original_price: product.sale_price, // ✨ السعر الافتراضي للمقارنة
          total: product.sale_price,
          max_quantity: product.quantity,
          image_url: product.image_url, // إضافة صورة المنتج
        },
      ]);
    }
  };

  // معالجة مسح الباركود
  const handleBarcodeScan = (barcode) => {
    // البحث عن المنتج بالباركود
    const product = products.find(p => 
      p.barcode === barcode || 
      p.code === barcode ||
      p.barcode?.toLowerCase() === barcode?.toLowerCase()
    );
    
    // إضافة للتاريخ
    const scanRecord = {
      barcode,
      time: new Date().toLocaleTimeString(language === "ar" ? "ar-IQ" : "en-US"),
      success: !!product,
      productName: product?.name || null
    };
    setScanHistory(prev => [scanRecord, ...prev.slice(0, 9)]); // الاحتفاظ بآخر 10 عمليات مسح
    
    if (product) {
      playBeepSound(true); // صوت نجاح
      setLastScannedProduct(product); // حفظ آخر منتج ممسوح
      addToCart(product);
      toast.success(
        language === "ar" 
          ? `✅ تم إضافة: ${product.name}` 
          : `✅ Added: ${product.name}`,
        { duration: 2000 }
      );
      
      // إخفاء آخر منتج بعد 5 ثواني
      setTimeout(() => setLastScannedProduct(null), 5000);
    } else {
      playBeepSound(false); // صوت خطأ
      setLastScannedProduct(null);
      toast.error(
        language === "ar" 
          ? `❌ لم يتم العثور على منتج بهذا الباركود: ${barcode}` 
          : `❌ Product not found with barcode: ${barcode}`,
        { duration: 3000 }
      );
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    const item = cart.find((i) => i.product_id === productId);
    // ✨ نسمح بتجاوز المخزون مع تحذير (بدلاً من المنع)
    if (item && item.max_quantity > 0 && newQuantity > item.max_quantity) {
      toast(
        () => (
          <span>
            ⚠️ {language === "ar"
              ? `تجاوزت الكمية المتوفرة (${item.max_quantity}) - سيظهر مخزون سالب`
              : `Exceeded available stock (${item.max_quantity})`}
          </span>
        ),
        { duration: 3500, icon: "⚠️", style: { background: "#92400e", color: "#fff" } }
      );
    }
    setCart(
      cart.map((item) =>
        item.product_id === productId
          ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
          : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.product_id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.total, 0);

  // دالة تنسيق رسالة الفاتورة للواتساب
  const formatWhatsAppReceipt = (sale, customer, shopName) => {
    const isArabic = language === "ar";
    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    
    let message = isArabic 
      ? `🧾 *فاتورة من ${shopName || 'المتجر'}*\n`
      : `🧾 *Receipt from ${shopName || 'Shop'}*\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    
    // معلومات العميل
    if (customer?.name) {
      message += isArabic ? `👤 الزبون: ${customer.name}\n` : `👤 Customer: ${customer.name}\n`;
    }
    
    // رقم الفاتورة والتاريخ
    message += isArabic 
      ? `📋 رقم: ${sale.id?.slice(0, 8) || '-'}\n`
      : `📋 #: ${sale.id?.slice(0, 8) || '-'}\n`;
    message += isArabic
      ? `📅 التاريخ: ${new Date().toLocaleDateString('ar-IQ')}\n`
      : `📅 Date: ${new Date().toLocaleDateString()}\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += isArabic ? `*المنتجات:*\n` : `*Items:*\n`;
    
    // المنتجات
    (sale.items || []).forEach((item, i) => {
      message += `${i + 1}. ${item.product_name}\n`;
      message += `   ${item.quantity} × ${formatNum(item.price)} = ${formatNum(item.total)} د.ع\n`;
    });
    
    message += `━━━━━━━━━━━━━━━\n`;
    
    // المجموع
    message += isArabic 
      ? `💰 *الإجمالي: ${formatNum(sale.total)} د.ع*\n`
      : `💰 *Total: ${formatNum(sale.total)} د.ع*\n`;
    
    // نوع الدفع
    const paymentLabel = sale.payment_type === 'cash' 
      ? (isArabic ? '✅ نقدي' : '✅ Cash')
      : (isArabic ? '📝 آجل' : '📝 Credit');
    message += `${paymentLabel}\n`;
    
    // إذا كان آجل، أضف المبلغ المدفوع والمتبقي
    if (sale.payment_type === 'credit') {
      const remaining = sale.total - (sale.amount_paid || 0);
      message += isArabic
        ? `💵 المدفوع: ${formatNum(sale.amount_paid || 0)} د.ع\n`
        : `💵 Paid: ${formatNum(sale.amount_paid || 0)} د.ع\n`;
      message += isArabic
        ? `⏳ المتبقي: ${formatNum(remaining)} د.ع\n`
        : `⏳ Remaining: ${formatNum(remaining)} د.ع\n`;
    }
    
    // نقاط الولاء
    if (sale.earned_points > 0) {
      message += `━━━━━━━━━━━━━━━\n`;
      message += isArabic
        ? `🎁 نقاط الولاء المكتسبة: ${sale.earned_points} نقطة\n`
        : `🎁 Loyalty points earned: ${sale.earned_points} points\n`;
    }
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += isArabic ? `شكراً لتعاملكم معنا! 🙏` : `Thank you for your business! 🙏`;
    
    return message;
  };

  // دالة إرسال الفاتورة عبر WhatsApp
  const sendWhatsAppReceipt = (sale, customer) => {
    if (!customer?.phone) return;
    
    // تنظيف رقم الهاتف
    let phone = customer.phone.replace(/\D/g, '');
    
    // إضافة رمز الدولة للعراق إذا لم يكن موجوداً
    if (phone.startsWith('07')) {
      phone = '964' + phone.substring(1);
    } else if (!phone.startsWith('964')) {
      phone = '964' + phone;
    }
    
    const message = formatWhatsAppReceipt(sale, customer, shopInfo?.name);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    
    // فتح WhatsApp في نافذة جديدة
    window.open(whatsappUrl, '_blank');
  };

  const handleHoldInvoice = async () => {
    if (cart.length === 0) {
      toast.error(language === "ar" ? "السلة فارغة" : "Cart is empty");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const customer = (customerInputMode === "select" && selectedCustomer)
        ? customers.find((c) => c.id === selectedCustomer)
        : null;
      const heldCustomerName = customer?.name
        || (customerInputMode === "manual" ? manualCustomerName.trim() : "");
      const items = cart.map((item) => ({
        product_id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.selling_price || item.price,
        purchase_price: item.purchase_price,
      }));
      const subtotal = cart.reduce((sum, i) => sum + (i.selling_price || i.price) * i.quantity, 0);
      const discount = calculateDiscount();
      const total = Math.max(0, subtotal - discount);
      await axios.post(
        `${API}/held-invoices`,
        {
          invoice_type: "sale",
          customer_id: (customerInputMode === "select" ? selectedCustomer : null) || null,
          customer_name: heldCustomerName || "",
          items,
          subtotal,
          discount,
          total,
          currency: "IQD",
          notes: "",
          payment_type: paymentType,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === "ar" ? "تم تعليق الفاتورة ✓" : "Invoice held ✓");
      setCart([]);
      setSelectedCustomer("");
      setManualCustomerName("");
      setManualCustomerPhone("");
    } catch (e) {
      toast.error(language === "ar" ? "فشل تعليق الفاتورة" : "Failed to hold");
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error(language === "ar" ? "السلة فارغة" : "Cart is empty");
      return;
    }

    if (paymentType === "credit") {
      const hasCustomer = customerInputMode === "select"
        ? !!selectedCustomer
        : !!manualCustomerName.trim();
      if (!hasCustomer) {
        toast.error(language === "ar"
          ? (customerInputMode === "select" ? "اختر الزبون" : "أدخل اسم الزبون")
          : "Customer required");
        return;
      }
    }

    setLoading(true);
    try {
      const customer = (customerInputMode === "select" && selectedCustomer)
        ? customers.find((c) => c.id === selectedCustomer)
        : null;
      const effectiveCustomerName = customer?.name
        || (customerInputMode === "manual" ? manualCustomerName.trim() : null);
      const effectiveCustomerPhone = customer?.phone
        || (customerInputMode === "manual" ? manualCustomerPhone.trim() : null);
      const saleData = {
        items: cart.map(({ product_id, product_name, quantity, price, total }) => ({
          product_id,
          product_name,
          quantity,
          price,
          total,
        })),
        total: cartTotal,
        payment_type: paymentType,
        customer_id: (customerInputMode === "select" ? selectedCustomer : null) || null,
        customer_name: effectiveCustomerName || null,
        customer_phone: effectiveCustomerPhone || null,
        amount_paid: paymentType === "cash" ? cartTotal : parseFloat(amountPaid) || 0,
        due_date: paymentType === "credit" && dueDate ? new Date(dueDate).toISOString() : null,
        exchange_rate: usdRate,  // freeze USD rate at invoice creation
        // ✨ POS enhancements
        credit_mode: paymentType === "credit" ? creditMode : null,
        installments_count: paymentType === "credit" && creditMode === "installment" ? installmentsCount : null,
        installment_period_days: paymentType === "credit" && creditMode === "installment" ? installmentPeriodDays : null,
        protection_level: protectionLevel,
        notes: saleNotes.trim() || null,
      };

      const response = await axios.post(`${API}/sales`, saleData);
      const isOfflinePending = response.data?._offline_pending === true;
      
      if (isOfflinePending) {
        toast.success(language === "ar" 
          ? "💾 تم البيع وحفظه محلياً — سيُرسل عند رجوع الإنترنت" 
          : "💾 Sale saved locally — will sync online", 
          { duration: 4000 });
        // skip extras that need server data
        setCart([]);
        setSelectedCustomer("");
        setManualCustomerName("");
        setManualCustomerPhone("");
        setAmountPaid("");
        setDueDate("");
        setLoading(false);
        return;
      }
      
      // Show earned points notification
      const earnedPoints = response.data.earned_points || 0;
      if (earnedPoints > 0 && selectedCustomer) {
        toast.success(
          language === "ar" 
            ? `تم البيع بنجاح! 🎉 كسب العميل ${earnedPoints} نقطة ولاء` 
            : `Sale completed! 🎉 Customer earned ${earnedPoints} loyalty points`,
          { duration: 4000 }
        );
      } else {
        toast.success(language === "ar" ? "تم البيع بنجاح ✅" : "Sale completed");
      }

      // ✨ إذا كانت الفاتورة بالتقسيط — تنبيه بأنها رُبطت تلقائياً بصفحة الأقساط
      if (response.data?.linked_installment_id) {
        toast.success(
          (tt) => (
            <span className="flex items-center gap-3">
              <span>
                {language === "ar"
                  ? "🔗 تم ربط الفاتورة بصفحة الأقساط تلقائياً"
                  : "🔗 Invoice auto-linked to installments page"}
              </span>
              <button
                onClick={() => { toast.dismiss(tt.id); navigate("/installments"); }}
                className="px-2 py-1 rounded bg-white/20 hover:bg-white/30 text-xs font-bold"
                data-testid="goto-installments-toast-btn"
              >
                {language === "ar" ? "عرض" : "View"}
              </button>
            </span>
          ),
          { duration: 6000, icon: "📅", style: { background: "#0ea5e9", color: "#fff", fontWeight: "bold" } }
        );
      }
      
      // رسالة تحفيزية للموظف
      setTimeout(() => {
        toast(getRandomMotivationalMessage(), {
          duration: 3000,
          icon: '💪',
          style: {
            background: '#10b981',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '16px',
          },
        });
      }, 800);
      
      // إرسال الفاتورة عبر WhatsApp تلقائياً إذا كان الخيار مفعّلاً
      const whatsappPhone = customer?.phone || (customerInputMode === "manual" ? manualCustomerPhone : null);
      const whatsappTarget = customer || (whatsappPhone ? { name: effectiveCustomerName || "", phone: whatsappPhone } : null);
      if (autoWhatsApp && whatsappTarget?.phone) {
        sendWhatsAppReceipt(response.data, whatsappTarget);
        toast.success(
          language === "ar" 
            ? "📱 جاري فتح WhatsApp لإرسال الفاتورة..." 
            : "📱 Opening WhatsApp to send receipt...",
          { duration: 2000 }
        );
      }
      
      // Show receipt dialog
      setCompletedSale({
        ...response.data,
        customer_phone: whatsappPhone || null,
      });
      setIsReceiptOpen(true);
      
      // Reset form
      setCart([]);
      setPaymentType("cash");
      setSelectedCustomer("");
      setManualCustomerName("");
      setManualCustomerPhone("");
      setCustomerInputMode("select");
      setCreditMode("credit_period");
      setSaleNotes("");
      setAmountPaid("");
      setDueDate("");
      setDiscount({ type: "none", value: 0 });
      setCouponCode("");
      setAppliedCoupon(null);
      setCashReceived("");
      fetchData();
      fetchTodayStats(); // تحديث إحصائيات اليوم بعد كل بيع
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  // حساب الخصم
  const calculateDiscount = () => {
    // أولوية للكوبون المطبق
    if (appliedCoupon) {
      return appliedCoupon.discount_amount;
    }
    if (discount.type === "none" || !discount.value) return 0;
    if (discount.type === "percentage") {
      return (cartTotal * discount.value) / 100;
    }
    return discount.value; // fixed amount
  };

  // المجموع بعد الخصم
  const discountAmount = calculateDiscount();
  const finalTotal = Math.max(0, cartTotal - discountAmount);

  // حساب الباقي
  const changeAmount = cashReceived ? parseFloat(cashReceived) - finalTotal : 0;

  // التحقق من الكوبون وتطبيقه
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error(language === "ar" ? "أدخل كود الكوبون" : "Enter coupon code");
      return;
    }
    
    if (cart.length === 0) {
      toast.error(language === "ar" ? "أضف منتجات للسلة أولاً" : "Add products to cart first");
      return;
    }

    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.toUpperCase(),
        order_total: cartTotal,
        customer_id: selectedCustomer || null,
      });
      
      setAppliedCoupon({
        code: response.data.coupon.code,
        discount_amount: response.data.discount_amount,
        discount_type: response.data.coupon.discount_type,
        discount_value: response.data.coupon.discount_value,
      });
      setDiscount({ type: "none", value: 0 }); // إزالة الخصم اليدوي
      
      toast.success(
        language === "ar" 
          ? `تم تطبيق الكوبون! خصم ${new Intl.NumberFormat('ar-IQ').format(response.data.discount_amount)} د.ع`
          : `Coupon applied! Discount ${new Intl.NumberFormat('en-US').format(response.data.discount_amount)} د.ع`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "ar" ? "كوبون غير صالح" : "Invalid coupon"));
    } finally {
      setCouponLoading(false);
    }
  };

  // إزالة الكوبون
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.success(language === "ar" ? "تم إزالة الكوبون" : "Coupon removed");
  };

  // تصفية المنتجات - البحث بالاسم أو الكود أو الباركود
  const filteredProducts = products.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="pos-page">
      {/* شريط الاختصارات */}
      {showShortcuts && (
        <div className="fixed top-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-40 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-emerald-400 font-bold flex items-center gap-2">
              ⌨️ {language === "ar" ? "اختصارات لوحة المفاتيح" : "Keyboard Shortcuts"}
            </h3>
            <button onClick={() => setShowShortcuts(false)} className="text-slate-400 hover:text-white">✕</button>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-slate-300"><span>F1</span><span>{language === "ar" ? "عرض/إخفاء الاختصارات" : "Toggle shortcuts"}</span></div>
            <div className="flex justify-between text-slate-300"><span>F2</span><span>{language === "ar" ? "فتح ماسح الكاميرا" : "Open camera scanner"}</span></div>
            <div className="flex justify-between text-slate-300"><span>F3</span><span>{language === "ar" ? "البحث" : "Search"}</span></div>
            <div className="flex justify-between text-slate-300"><span>F4</span><span>{language === "ar" ? "إتمام البيع" : "Checkout"}</span></div>
            <div className="flex justify-between text-slate-300"><span>F5</span><span>{language === "ar" ? "تبديل نوع الدفع" : "Toggle payment"}</span></div>
            <div className="flex justify-between text-emerald-400"><span>F6</span><span>{language === "ar" ? "وضع الماسح USB" : "USB Scanner Mode"}</span></div>
            <div className="flex justify-between text-slate-300"><span>Esc</span><span>{language === "ar" ? "مسح السلة / إلغاء وضع الماسح" : "Clear cart / Cancel scanner mode"}</span></div>
            <div className="border-t border-slate-700 pt-2 mt-2">
              <p className="text-emerald-400 text-xs mb-2">{language === "ar" ? "🔥 ماسح الباركود USB:" : "🔥 USB Barcode Scanner:"}</p>
              <p className="text-slate-400 text-xs">{language === "ar" ? "• يعمل تلقائياً عندما لا يكون المؤشر في حقل إدخال" : "• Works automatically when not focused on input field"}</p>
              <p className="text-slate-400 text-xs">{language === "ar" ? "• اضغط F6 أو زر USB لوضع المسح المتواصل" : "• Press F6 or USB button for continuous scan mode"}</p>
            </div>
          </div>
        </div>
      )}

      {/* تقرير نهاية اليوم */}
      {showEndOfDayReport && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowEndOfDayReport(false)}>
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-6 w-full max-w-md animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FileText className="w-6 h-6 text-emerald-400" />
                {language === "ar" ? "تقرير نهاية اليوم" : "End of Day Report"}
              </h2>
              <button onClick={() => setShowEndOfDayReport(false)} className="text-slate-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* التاريخ */}
              <div className="text-center text-slate-400 text-sm">
                📅 {new Date().toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              
              {/* إحصائيات اليوم */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                  <p className="text-emerald-400 text-2xl font-bold">
                    {formatCurrency(todayStats?.sales || 0)}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "إجمالي المبيعات" : "Total Sales"}</p>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                  <p className="text-blue-400 text-2xl font-bold">
                    {todayStats?.transactions || 0}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "عدد المعاملات" : "Transactions"}</p>
                </div>
                
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-center">
                  <p className="text-purple-400 text-2xl font-bold">
                    {formatCurrency(todayStats?.profit || 0)}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "صافي الربح" : "Net Profit"}</p>
                </div>
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
                  <p className="text-amber-400 text-2xl font-bold">
                    {formatCurrency(todayStats?.expenses || 0)}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "المصروفات" : "Expenses"}</p>
                </div>
              </div>
              
              {/* ملخص */}
              <div className="bg-slate-900/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-slate-300">
                  <span>{language === "ar" ? "المبيعات:" : "Sales:"}</span>
                  <span className="text-emerald-400">{formatCurrency(todayStats?.sales || 0)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>{language === "ar" ? "المشتريات:" : "Purchases:"}</span>
                  <span className="text-red-400">-{formatCurrency(todayStats?.purchases || 0)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>{language === "ar" ? "المصروفات:" : "Expenses:"}</span>
                  <span className="text-red-400">-{formatCurrency(todayStats?.expenses || 0)}</span>
                </div>
                <div className="border-t border-slate-700 pt-2 flex justify-between font-bold">
                  <span className="text-white">{language === "ar" ? "صافي الربح:" : "Net Profit:"}</span>
                  <span className={todayStats?.profit >= 0 ? "text-emerald-400" : "text-red-400"}>
                    {formatCurrency(todayStats?.profit || 0)}
                  </span>
                </div>
              </div>
              
              {/* أزرار الإجراءات */}
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={printEndOfDayReport}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="print-eod-report-btn"
                >
                  <Printer className="w-4 h-4 me-2" />
                  {language === "ar" ? "طباعة" : "Print"}
                </Button>
                <Button 
                  onClick={shareEndOfDayReport}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="share-eod-report-btn"
                >
                  <Share2 className="w-4 h-4 me-2" />
                  {language === "ar" ? "مشاركة" : "Share"}
                </Button>
              </div>
              
              {/* زر الإغلاق */}
              <Button 
                onClick={() => setShowEndOfDayReport(false)} 
                className="w-full bg-slate-700 hover:bg-slate-600"
              >
                {language === "ar" ? "إغلاق" : "Close"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* تقرير الوردية */}
      {showShiftReport && shiftReport && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowShiftReport(false)}>
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-6 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Timer className="w-6 h-6 text-purple-400" />
                {language === "ar" ? "تقرير الوردية" : "Shift Report"}
              </h2>
              <button onClick={() => setShowShiftReport(false)} className="text-slate-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* معلومات الموظف والوردية */}
              <div className="text-center p-3 rounded-xl bg-purple-500/10 border border-purple-500/30">
                <p className="text-purple-400 text-lg font-bold flex items-center justify-center gap-2">
                  <Users className="w-5 h-5" />
                  {shiftReport.employee_name}
                </p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <select
                    value={shiftHours}
                    onChange={(e) => setShiftHours(parseInt(e.target.value))}
                    className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1 text-white text-sm"
                  >
                    <option value={4}>{language === "ar" ? "4 ساعات" : "4 hours"}</option>
                    <option value={6}>{language === "ar" ? "6 ساعات" : "6 hours"}</option>
                    <option value={8}>{language === "ar" ? "8 ساعات" : "8 hours"}</option>
                    <option value={10}>{language === "ar" ? "10 ساعات" : "10 hours"}</option>
                    <option value={12}>{language === "ar" ? "12 ساعة" : "12 hours"}</option>
                  </select>
                  <Button
                    size="sm"
                    onClick={fetchShiftReport}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {language === "ar" ? "تحديث" : "Refresh"}
                  </Button>
                </div>
              </div>
              
              {/* إحصائيات الوردية */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                  <p className="text-emerald-400 text-2xl font-bold">
                    {formatCurrency(shiftReport.total_sales || 0)}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "إجمالي المبيعات" : "Total Sales"}</p>
                </div>
                
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-center">
                  <p className="text-purple-400 text-2xl font-bold">
                    {shiftReport.transaction_count || 0}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "عدد المعاملات" : "Transactions"}</p>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                  <p className="text-blue-400 text-2xl font-bold">
                    {formatCurrency(shiftReport.cash_sales || 0)}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "مبيعات نقدية" : "Cash Sales"}</p>
                </div>
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
                  <p className="text-amber-400 text-2xl font-bold">
                    {formatCurrency(shiftReport.credit_sales || 0)}
                  </p>
                  <p className="text-slate-400 text-sm">{language === "ar" ? "مبيعات آجلة" : "Credit Sales"}</p>
                </div>
              </div>
              
              {/* المنتجات المباعة */}
              {shiftReport.top_products?.length > 0 && (
                <div className="bg-slate-900/50 rounded-xl p-4">
                  <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4 text-amber-400" />
                    {language === "ar" ? "المنتجات المباعة" : "Products Sold"}
                    <span className="text-slate-400 text-sm font-normal">({shiftReport.items_sold} {language === "ar" ? "قطعة" : "items"})</span>
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {shiftReport.top_products.map((product, index) => (
                      <div key={index} className="flex justify-between text-sm p-2 rounded bg-slate-800/50">
                        <span className="text-slate-300">{product.name} <span className="text-slate-500">×{product.quantity}</span></span>
                        <span className="text-emerald-400">{formatCurrency(product.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* أزرار الإجراءات */}
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={printShiftReport}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="print-shift-report-btn"
                >
                  <Printer className="w-4 h-4 me-2" />
                  {language === "ar" ? "طباعة" : "Print"}
                </Button>
                <Button 
                  onClick={shareShiftReport}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="share-shift-report-btn"
                >
                  <Share2 className="w-4 h-4 me-2" />
                  {language === "ar" ? "مشاركة" : "Share"}
                </Button>
              </div>
              
              {/* زر الإغلاق */}
              <Button 
                onClick={() => setShowShiftReport(false)} 
                className="w-full bg-slate-700 hover:bg-slate-600"
              >
                {language === "ar" ? "إغلاق" : "Close"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* رأس الصفحة - مبسط للكاشير */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-heading font-bold text-white">{t("pos")}</h1>
          {/* مؤشر وضع الماسح */}
          {scannerMode && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/50 animate-pulse">
              <ScanLine className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">
                {language === "ar" ? "وضع المسح" : "Scan Mode"}
              </span>
              <button 
                onClick={() => setScannerMode(false)}
                className="text-emerald-400 hover:text-emerald-300 ms-1"
                title={language === "ar" ? "إلغاء (Escape)" : "Cancel (Escape)"}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        
        {/* الأدوات الأساسية فقط للكاشير */}
        <div className="flex flex-wrap items-center gap-3">
          {/* تصفّح الفواتير السابقة */}
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-800/50 border border-emerald-500/30" data-testid="pos-invoice-nav">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateInvoice("first")}
              className="h-7 w-7 text-emerald-400 hover:bg-emerald-500/20"
              title={language === "ar" ? "أول فاتورة" : "First invoice"}
              data-testid="pos-nav-first-btn"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateInvoice("prev")}
              className="h-7 w-7 text-emerald-400 hover:bg-emerald-500/20"
              title={language === "ar" ? "الفاتورة السابقة" : "Previous"}
              data-testid="pos-nav-prev-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateInvoice(viewedSale ? null : "first")}
              className="h-7 px-2 text-emerald-300 text-xs gap-1"
              title={language === "ar" ? "تصفّح الفواتير" : "Browse invoices"}
              data-testid="pos-nav-browse-btn"
            >
              <History className="w-3.5 h-3.5" />
              {language === "ar" ? "الفواتير" : "Invoices"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateInvoice("next")}
              className="h-7 w-7 text-emerald-400 hover:bg-emerald-500/20"
              title={language === "ar" ? "الفاتورة التالية" : "Next"}
              data-testid="pos-nav-next-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateInvoice("last")}
              className="h-7 w-7 text-emerald-400 hover:bg-emerald-500/20"
              title={language === "ar" ? "آخر فاتورة" : "Last invoice"}
              data-testid="pos-nav-last-btn"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
          </div>

          {/* قائمة التقارير المخفية - زر واحد ينظف الواجهة */}
          <div className="relative group">
            <Button
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-400 hover:bg-slate-700/50"
              data-testid="pos-reports-menu-btn"
              title={language === "ar" ? "تقارير وأدوات إضافية" : "Reports & extra tools"}
            >
              <FileText className="w-4 h-4 me-1" />
              {language === "ar" ? "تقارير" : "Reports"}
            </Button>
            <div className="absolute end-0 top-full mt-1 hidden group-hover:flex group-focus-within:flex flex-col bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 min-w-[180px]">
              <button
                onClick={() => setShowEndOfDayReport(true)}
                className="text-end px-3 py-2 text-sm text-amber-400 hover:bg-slate-700/50 transition-colors"
                data-testid="end-of-day-report-btn"
              >
                <FileText className="w-4 h-4 inline-block me-1" />
                {language === "ar" ? "تقرير اليوم" : "Day Report"}
              </button>
              <button
                onClick={fetchShiftReport}
                className="text-end px-3 py-2 text-sm text-purple-400 hover:bg-slate-700/50 transition-colors border-t border-slate-700"
                data-testid="shift-report-btn"
              >
                <Timer className="w-4 h-4 inline-block me-1" />
                {language === "ar" ? "تقرير الوردية" : "Shift Report"}
              </button>
            </div>
          </div>
          
          {/* اختصارات لوحة المفاتيح */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowShortcuts(!showShortcuts)}
            className="text-slate-400 hover:text-white"
            title="F1"
          >
            ⌨️
          </Button>
        </div>
      </div>

      {/* تم حذف "Quick Sale Buttons" المكرر - نوع الدفع موجود في شاشة الدفع (Checkout) مباشرة */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products Section */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search and Scan */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400`} />
                  <Input
                    placeholder={language === "ar" ? "بحث أو مسح باركود..." : "Search or scan barcode..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && searchTerm.trim()) {
                        // تحقق إذا كان باركود
                        const product = products.find(p => 
                          p.barcode === searchTerm.trim() || 
                          p.code === searchTerm.trim()
                        );
                        if (product) {
                          e.preventDefault();
                          addToCart(product);
                          setSearchTerm("");
                          toast.success(
                            language === "ar" 
                              ? `✅ تم إضافة: ${product.name}` 
                              : `✅ Added: ${product.name}`
                          );
                        }
                      }
                    }}
                    className={`${language === "ar" ? "pr-10" : "pl-10"} bg-slate-900 border-slate-700 text-white`}
                    data-testid="pos-search-input"
                  />
                </div>
                {/* زر مسح الباركود */}
                <Button
                  onClick={() => setIsScannerOpen(true)}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4"
                  data-testid="scan-barcode-btn"
                  title={language === "ar" ? "مسح بالكاميرا (F2)" : "Scan with camera (F2)"}
                >
                  <Camera className="w-5 h-5 me-2" />
                  <span className="hidden sm:inline">
                    {language === "ar" ? "كاميرا" : "Camera"}
                  </span>
                </Button>
                {/* زر وضع الماسح USB المتواصل */}
                <Button
                  onClick={() => setScannerMode(!scannerMode)}
                  className={`px-4 transition-all ${
                    scannerMode 
                      ? "bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-400 ring-offset-2 ring-offset-slate-900 animate-pulse" 
                      : "bg-slate-700 hover:bg-slate-600"
                  }`}
                  data-testid="usb-scanner-mode-btn"
                  title={language === "ar" ? "وضع الماسح USB - Escape للإلغاء" : "USB Scanner Mode - Escape to cancel"}
                >
                  <ScanLine className={`w-5 h-5 me-2 ${scannerMode ? "animate-pulse" : ""}`} />
                  <span className="hidden sm:inline">
                    {scannerMode 
                      ? (language === "ar" ? "جاهز للمسح ✓" : "Ready ✓")
                      : (language === "ar" ? "USB" : "USB")
                    }
                  </span>
                </Button>
              </div>
              {/* تلميح للمستخدم */}
              <div className={`text-xs mt-2 flex items-center gap-2 transition-all ${
                scannerMode 
                  ? "bg-emerald-500/20 border border-emerald-500/50 rounded-lg px-3 py-2" 
                  : "text-slate-500"
              }`}>
                <ScanLine className={`w-3 h-3 ${scannerMode ? "text-emerald-400 animate-pulse" : ""}`} />
                {scannerMode ? (
                  <span className="text-emerald-400 font-medium">
                    {language === "ar" 
                      ? "🟢 وضع الماسح USB نشط - امسح الباركود الآن! (Escape للإلغاء)" 
                      : "🟢 USB Scanner Mode Active - Scan now! (Escape to cancel)"}
                  </span>
                ) : (
                  <span>
                    {language === "ar" 
                      ? "💡 استخدم قارئ الباركود USB مباشرة، أو اضغط زر USB لوضع المسح المتواصل" 
                      : "💡 Use USB barcode scanner directly, or click USB button for continuous scan mode"}
                  </span>
                )}
                {/* زر تبديل الصوت */}
                <button
                  onClick={() => setScanSound(!scanSound)}
                  className={`ms-auto p-1 rounded ${scanSound ? 'text-emerald-400' : 'text-slate-500'}`}
                  title={language === "ar" ? (scanSound ? "إيقاف الصوت" : "تشغيل الصوت") : (scanSound ? "Mute" : "Unmute")}
                >
                  {scanSound ? "🔊" : "🔇"}
                </button>
              </div>

              {/* آخر منتج ممسوح */}
              {lastScannedProduct && (
                <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/40 animate-pulse">
                  <div className="flex items-center gap-3">
                    {lastScannedProduct.image_url ? (
                      <img src={lastScannedProduct.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Package className="w-6 h-6 text-emerald-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-emerald-400 font-bold text-sm">{lastScannedProduct.name}</p>
                      <p className="text-slate-400 text-xs">
                        {language === "ar" ? "السعر:" : "Price:"} {lastScannedProduct.sale_price?.toLocaleString()} 
                        {language === "ar" ? " د.ع" : " IQD"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 text-xs">{language === "ar" ? "✅ تمت الإضافة" : "✅ Added"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* تاريخ المسح */}
              {scanHistory.length > 0 && scannerMode && (
                <div className="mt-3 p-2 rounded-lg bg-slate-800/50 border border-slate-700 max-h-32 overflow-y-auto">
                  <p className="text-xs text-slate-400 mb-2">{language === "ar" ? "📋 آخر عمليات المسح:" : "📋 Recent scans:"}</p>
                  <div className="space-y-1">
                    {scanHistory.map((scan, idx) => (
                      <div key={idx} className={`text-xs flex items-center gap-2 ${scan.success ? 'text-emerald-400' : 'text-red-400'}`}>
                        <span>{scan.success ? '✅' : '❌'}</span>
                        <span className="font-mono">{scan.barcode}</span>
                        {scan.productName && <span className="text-slate-400">- {scan.productName}</span>}
                        <span className="ms-auto text-slate-500">{scan.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* المنتجات الأكثر مبيعاً - Quick Access */}
          {topProducts.length > 0 && !searchTerm && (
            <div className="mb-4">
              <h3 className="text-slate-400 text-sm mb-2 flex items-center gap-2">
                ⭐ {language === "ar" ? "الأكثر مبيعاً هذا الأسبوع" : "Best sellers this week"}
              </h3>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {topProducts.map((tp) => {
                  const product = products.find(p => p.id === tp.product_id);
                  if (!product) return null;
                  return (
                    <button
                      key={tp.product_id}
                      onClick={() => addToCart(product)}
                      disabled={product.quantity <= 0}
                      className="flex-shrink-0 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 text-amber-400 text-sm hover:from-amber-500/30 hover:to-orange-500/30 transition-all disabled:opacity-50"
                    >
                      ⭐ {product.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredProducts.map((product) => (
              <button
                key={product.id}
                onClick={() => addToCart(product)}
                disabled={product.quantity <= 0}
                className={`p-4 rounded-lg border text-start transition-all ${
                  product.quantity <= 0
                    ? "bg-slate-900/50 border-slate-700 opacity-50 cursor-not-allowed"
                    : "bg-slate-800/50 border-slate-700 hover:border-amber-500/50 hover:bg-slate-800 cursor-pointer"
                }`}
                data-testid={`product-card-${product.id}`}
              >
                {/* صورة المنتج أو أيقونة افتراضية */}
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-20 object-cover rounded-lg mb-2"
                  />
                ) : (
                  <Package className="w-8 h-8 text-amber-500 mb-2" />
                )}
                <p className="text-white font-medium text-sm truncate">
                  {product.name}
                </p>
                <p className="text-slate-400 text-xs mt-1">{product.code}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-green-500 font-bold text-sm">
                    {formatCurrency(product.sale_price)}
                  </span>
                  <span
                    className={`text-xs ${
                      product.quantity <= product.min_stock
                        ? "text-red-500"
                        : "text-slate-400"
                    }`}
                  >
                    {product.quantity}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Cart Section */}
        <div className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-amber-500" />
                {language === "ar" ? "السلة" : "Cart"}
                {cart.length > 0 && (
                  <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {cart.length}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {cart.length === 0 ? (
                <div className="space-y-3">
                  <div className="text-center py-8 text-slate-400">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === "ar" ? "السلة فارغة" : "Cart is empty"}</p>
                  </div>
                  {/* زر الفواتير المعلقة متاح حتى مع سلة فارغة */}
                  <Button
                    onClick={openHeldInvoicesModal}
                    variant="outline"
                    className="w-full border-purple-500/40 text-purple-400 hover:bg-purple-500/10 py-3"
                    data-testid="held-invoices-list-btn-empty"
                  >
                    📂 {language === "ar" ? "عرض الفواتير المعلقة" : "View Held Invoices"}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {cart.map((item) => (
                      <div
                        key={item.product_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-700"
                      >
                        {/* صورة المنتج في السلة */}
                        {item.image_url && (
                          <img 
                            src={item.image_url} 
                            alt={item.product_name}
                            className="w-10 h-10 object-cover rounded-lg me-2"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {item.product_name}
                          </p>
                          {/* ✨ سعر قابل للتعديل لكل مادة على حدة */}
                          <div className="flex items-center gap-1 mt-1">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price}
                              onChange={(e) => {
                                const newPrice = parseFloat(e.target.value) || 0;
                                setCart(cart.map((c) =>
                                  c.product_id === item.product_id
                                    ? { ...c, price: newPrice, total: c.quantity * newPrice }
                                    : c
                                ));
                              }}
                              className="text-amber-500 text-xs w-20 bg-slate-800 border border-slate-600 rounded px-1 py-0.5 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              data-testid={`cart-price-${item.product_id}`}
                              title={language === "ar" ? "سعر البيع لهذه المادة فقط" : "Per-item sale price"}
                            />
                            {item.original_price && item.original_price !== item.price && (
                              <span className="text-xs text-slate-500 line-through">
                                {formatCurrency(item.original_price)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-slate-400 hover:text-white"
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              updateQuantity(item.product_id, val);
                            }}
                            className={`w-12 text-center border rounded px-1 py-0.5 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                              item.max_quantity > 0 && item.quantity > item.max_quantity
                                ? "text-amber-300 bg-amber-900/30 border-amber-500"
                                : "text-white bg-slate-800 border-slate-600"
                            }`}
                            data-testid={`cart-quantity-${item.product_id}`}
                            title={item.max_quantity > 0 && item.quantity > item.max_quantity
                              ? (language === "ar" ? `المخزون الحالي: ${item.max_quantity}` : `Stock: ${item.max_quantity}`)
                              : ""}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-slate-400 hover:text-white"
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-red-500 hover:text-red-400"
                            onClick={() => removeFromCart(item.product_id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className="border-t border-slate-700 pt-4 space-y-3">
                    {/* المجموع الأساسي */}
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">{language === "ar" ? "المجموع الفرعي" : "Subtotal"}</span>
                      <span className="text-slate-300">{formatCurrency(cartTotal)}</span>
                    </div>
                    
                    {/* نظام الخصومات */}
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-purple-400 text-sm">🏷️ {language === "ar" ? "خصم" : "Discount"}</span>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={discount.type}
                          onChange={(e) => {
                            setDiscount({ ...discount, type: e.target.value });
                            if (e.target.value !== "none") {
                              setAppliedCoupon(null);
                              setCouponCode("");
                            }
                          }}
                          className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-white text-sm"
                          disabled={!!appliedCoupon}
                        >
                          <option value="none">{language === "ar" ? "بدون خصم" : "No discount"}</option>
                          <option value="percentage">{language === "ar" ? "نسبة %" : "Percentage %"}</option>
                          <option value="fixed">{language === "ar" ? "مبلغ ثابت" : "Fixed amount"}</option>
                        </select>
                        {discount.type === "percentage" && !appliedCoupon && (
                          <Input
                            type="number"
                            placeholder="%"
                            value={discount.value || ""}
                            onChange={(e) => setDiscount({ ...discount, value: parseFloat(e.target.value) || 0 })}
                            className="w-24 bg-slate-900 border-slate-700 text-white text-sm"
                          />
                        )}
                        {discount.type === "fixed" && !appliedCoupon && (
                          <div className="w-40">
                            <PriceInput
                              value={discount.value || 0}
                              onChange={(v) => setDiscount({ ...discount, value: v })}
                              placeholder="خصم"
                              testId="discount-fixed-input"
                              className="bg-slate-900 border-slate-700 text-white text-sm"
                            />
                          </div>
                        )}
                      </div>
                      {discountAmount > 0 && !appliedCoupon && (
                        <div className="flex justify-between mt-2 text-purple-400 text-sm">
                          <span>{language === "ar" ? "قيمة الخصم:" : "Discount:"}</span>
                          <span>-{formatCurrency(discountAmount)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* كوبون الخصم */}
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Ticket className="w-4 h-4 text-amber-400" />
                        <span className="text-amber-400 text-sm">{language === "ar" ? "كوبون خصم" : "Discount Coupon"}</span>
                      </div>
                      
                      {appliedCoupon ? (
                        <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-emerald-400" />
                            <span className="font-mono font-bold text-emerald-400">{appliedCoupon.code}</span>
                            <span className="text-emerald-400 text-sm">
                              ({appliedCoupon.discount_type === "percentage" 
                                ? `${appliedCoupon.discount_value}%` 
                                : formatCurrency(appliedCoupon.discount_value)})
                            </span>
                          </div>
                          <button
                            onClick={removeCoupon}
                            className="p-1 rounded hover:bg-red-500/20 text-red-400"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            placeholder={language === "ar" ? "أدخل كود الكوبون" : "Enter coupon code"}
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            className="flex-1 bg-slate-900 border-slate-700 text-white uppercase"
                            disabled={discount.type !== "none"}
                            data-testid="coupon-input"
                          />
                          <Button
                            type="button"
                            onClick={validateCoupon}
                            disabled={couponLoading || !couponCode.trim() || discount.type !== "none"}
                            className="bg-amber-600 hover:bg-amber-700 text-white px-3"
                            data-testid="apply-coupon-btn"
                          >
                            {couponLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              language === "ar" ? "تطبيق" : "Apply"
                            )}
                          </Button>
                        </div>
                      )}
                      
                      {appliedCoupon && (
                        <div className="flex justify-between mt-2 text-emerald-400 text-sm font-medium">
                          <span>{language === "ar" ? "خصم الكوبون:" : "Coupon discount:"}</span>
                          <span>-{formatCurrency(appliedCoupon.discount_amount)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* المجموع النهائي */}
                    <div className="flex items-center justify-between text-xl font-bold">
                      <span className="text-white">{t("total")}</span>
                      <div className="flex flex-col items-end">
                        <span className="text-emerald-400">{formatCurrency(finalTotal)}</span>
                        <USDHint amount={finalTotal} />
                      </div>
                    </div>
                    
                    {/* حاسبة الباقي (للدفع النقدي) */}
                    {paymentType === "cash" && (
                      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-blue-400 text-sm">💵 {language === "ar" ? "حاسبة الباقي" : "Change Calculator"}</span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <div className="flex-1">
                            <PriceInput
                              value={parseFloat(cashReceived) || 0}
                              onChange={(v) => setCashReceived(String(v))}
                              placeholder={language === "ar" ? "المبلغ المستلم" : "Cash received"}
                              testId="cash-received-input"
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                          </div>
                        </div>
                        {cashReceived && parseFloat(cashReceived) >= finalTotal && (
                          <div className="flex justify-between mt-2 text-lg font-bold">
                            <span className="text-blue-400">{language === "ar" ? "الباقي:" : "Change:"}</span>
                            <span className="text-green-400">{formatCurrency(changeAmount)}</span>
                          </div>
                        )}
                        {cashReceived && parseFloat(cashReceived) < finalTotal && (
                          <div className="flex justify-between mt-2 text-sm">
                            <span className="text-red-400">{language === "ar" ? "المبلغ غير كافٍ" : "Insufficient amount"}</span>
                            <span className="text-red-400">-{formatCurrency(finalTotal - parseFloat(cashReceived))}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* تم إخفاء "نقاط مكتسبة" و "Active Offer Banner" لتنظيف واجهة الكاشير
                        النقاط لاتزال تُحسب وتُحفظ تلقائياً، تظهر للزبون في الفاتورة المطبوعة */}
                  </div>

                  {/* Payment Type */}
                  <div className="space-y-2">
                    <Label className="text-slate-200">{t("paymentType")}</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={paymentType === "cash" ? "default" : "outline"}
                        onClick={() => setPaymentType("cash")}
                        className={
                          paymentType === "cash"
                            ? "bg-green-600 hover:bg-green-700"
                            : "border-slate-600 text-slate-300"
                        }
                        data-testid="cash-payment-btn"
                      >
                        <DollarSign className="w-4 h-4 me-1" />
                        {t("cash")}
                      </Button>
                      <Button
                        variant={paymentType === "credit" ? "default" : "outline"}
                        onClick={() => setPaymentType("credit")}
                        className={
                          paymentType === "credit"
                            ? "bg-amber-600 hover:bg-amber-700"
                            : "border-slate-600 text-slate-300"
                        }
                        data-testid="credit-payment-btn"
                      >
                        <CreditCard className="w-4 h-4 me-1" />
                        {t("credit")}
                      </Button>
                    </div>
                  </div>

                  {/* Customer Selection for Credit */}
                  {paymentType === "credit" && (
                    <div className="space-y-3">
                      {/* ✨ Credit Mode Selector — أزرار صريحة لطرق الدفع الآجل */}
                      <div className="space-y-2">
                        <Label className="text-slate-200">{language === "ar" ? "نوع الدفع الآجل" : "Credit Type"}</Label>
                        <div className="grid grid-cols-3 gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant={creditMode === "credit_period" ? "default" : "outline"}
                            onClick={() => setCreditMode("credit_period")}
                            className={creditMode === "credit_period" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                            data-testid="credit-mode-period-btn"
                          >
                            ⏳ {language === "ar" ? "آجل بفترة" : "Period"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={creditMode === "installment" ? "default" : "outline"}
                            onClick={() => setCreditMode("installment")}
                            className={creditMode === "installment" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                            data-testid="credit-mode-installment-btn"
                          >
                            📅 {language === "ar" ? "أقساط" : "Installments"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={creditMode === "partial" ? "default" : "outline"}
                            onClick={() => setCreditMode("partial")}
                            className={creditMode === "partial" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                            data-testid="credit-mode-partial-btn"
                          >
                            💵 {language === "ar" ? "جزئي" : "Partial"}
                          </Button>
                        </div>
                      </div>

                      {/* Customer source toggle — اختيار من القائمة أو إدخال يدوي */}
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant={customerInputMode === "select" ? "default" : "outline"}
                          onClick={() => setCustomerInputMode("select")}
                          className={customerInputMode === "select" ? "bg-emerald-600 hover:bg-emerald-700 text-xs flex-1" : "border-slate-600 text-slate-300 text-xs flex-1"}
                          data-testid="customer-mode-select-btn"
                        >
                          📋 {language === "ar" ? "من القائمة" : "From list"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={customerInputMode === "manual" ? "default" : "outline"}
                          onClick={() => setCustomerInputMode("manual")}
                          className={customerInputMode === "manual" ? "bg-emerald-600 hover:bg-emerald-700 text-xs flex-1" : "border-slate-600 text-slate-300 text-xs flex-1"}
                          data-testid="customer-mode-manual-btn"
                        >
                          ✍️ {language === "ar" ? "إدخال يدوي" : "Manual entry"}
                        </Button>
                      </div>

                      {customerInputMode === "select" ? (
                        <div className="space-y-2">
                          <Label className="text-slate-200">{t("customer")}</Label>
                          <Select
                            value={selectedCustomer}
                            onValueChange={setSelectedCustomer}
                          >
                            <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="customer-select">
                              <SelectValue placeholder={language === "ar" ? "اختر الزبون" : "Select customer"} />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              {customers.map((customer) => (
                                <SelectItem
                                  key={customer.id}
                                  value={customer.id}
                                  className="text-white hover:bg-slate-700"
                                >
                                  <span className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    {customer.name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label className="text-slate-200">{language === "ar" ? "اسم الزبون" : "Customer name"}</Label>
                          <Input
                            value={manualCustomerName}
                            onChange={(e) => setManualCustomerName(e.target.value)}
                            placeholder={language === "ar" ? "اكتب اسم الزبون..." : "Type customer name..."}
                            className="bg-slate-900 border-slate-700 text-white"
                            data-testid="manual-customer-name-input"
                          />
                          <Label className="text-slate-200">{language === "ar" ? "رقم الهاتف (اختياري)" : "Phone (optional)"}</Label>
                          <Input
                            value={manualCustomerPhone}
                            onChange={(e) => setManualCustomerPhone(e.target.value)}
                            placeholder="07XXXXXXXXX"
                            className="bg-slate-900 border-slate-700 text-white"
                            data-testid="manual-customer-phone-input"
                            inputMode="tel"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-slate-200">
                          {creditMode === "partial" 
                            ? (language === "ar" ? "المبلغ المدفوع جزئياً" : "Partial amount paid")
                            : t("amountPaid")}
                        </Label>
                        <PriceInput
                          value={parseFloat(amountPaid) || 0}
                          onChange={(v) => setAmountPaid(String(v))}
                          placeholder="0"
                          testId="amount-paid-input"
                          className="bg-slate-900 border-slate-700 text-white"
                        />
                      </div>

                      {/* Installment fields — تظهر فقط عند اختيار أقساط */}
                      {creditMode === "installment" && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-slate-200 text-xs">{language === "ar" ? "عدد الأقساط" : "Installments"}</Label>
                            <Input
                              type="number"
                              min="1"
                              value={installmentsCount}
                              onChange={(e) => setInstallmentsCount(parseInt(e.target.value) || 1)}
                              className="bg-slate-900 border-slate-700 text-white"
                              data-testid="installments-count-input"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-slate-200 text-xs">{language === "ar" ? "كل (يوم)" : "Every (days)"}</Label>
                            <Input
                              type="number"
                              min="1"
                              value={installmentPeriodDays}
                              onChange={(e) => setInstallmentPeriodDays(parseInt(e.target.value) || 1)}
                              className="bg-slate-900 border-slate-700 text-white"
                              data-testid="installment-period-input"
                            />
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-slate-200 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {creditMode === "installment" 
                            ? (language === "ar" ? "تاريخ أول قسط" : "First installment date")
                            : t("dueDate")}
                        </Label>
                        <Input
                          type="date"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          className="bg-slate-900 border-slate-700 text-white"
                          data-testid="due-date-input"
                        />
                      </div>
                    </div>
                  )}


                  {/* ✨ Notes — ملاحظات على الفاتورة */}
                  <div className="space-y-2">
                    <Label className="text-slate-200 flex items-center gap-2 text-sm">
                      📝 {language === "ar" ? "ملاحظات (اختياري)" : "Notes (optional)"}
                    </Label>
                    <textarea
                      value={saleNotes}
                      onChange={(e) => setSaleNotes(e.target.value)}
                      rows={2}
                      placeholder={language === "ar"
                        ? "مثال: تسليم خلال 3 أيام، ضمان..."
                        : "e.g. delivery in 3 days, warranty..."}
                      className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                      data-testid="pos-sale-notes-input"
                    />
                  </div>

                  {/* ✨ Protection Level — مستوى حماية الفاتورة */}
                  <div className="space-y-2 p-3 rounded-lg bg-slate-900/60 border border-slate-700">
                    <Label className="text-slate-200 flex items-center gap-2 text-sm">
                      🛡️ {language === "ar" ? "مستوى حماية الفاتورة" : "Invoice protection level"}
                    </Label>
                    <div className="grid grid-cols-3 gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant={protectionLevel === "weak" ? "default" : "outline"}
                        onClick={() => setProtectionLevel("weak")}
                        className={protectionLevel === "weak" ? "bg-slate-500 hover:bg-slate-600 text-xs" : "border-slate-600 text-slate-400 text-xs"}
                        data-testid="protection-weak-btn"
                      >
                        🔓 {language === "ar" ? "ضعيف" : "Weak"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={protectionLevel === "medium" ? "default" : "outline"}
                        onClick={() => setProtectionLevel("medium")}
                        className={protectionLevel === "medium" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                        data-testid="protection-medium-btn"
                      >
                        🔐 {language === "ar" ? "متوسط" : "Medium"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={protectionLevel === "strong" ? "default" : "outline"}
                        onClick={() => setProtectionLevel("strong")}
                        className={protectionLevel === "strong" ? "bg-emerald-600 hover:bg-emerald-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                        data-testid="protection-strong-btn"
                      >
                        🛡️ {language === "ar" ? "قوي" : "Strong"}
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      {protectionLevel === "weak" && (language === "ar" ? "يمكن تعديل/حذف الفاتورة" : "Editable/deletable")}
                      {protectionLevel === "medium" && (language === "ar" ? "يتطلب مرتجع لإلغاء البيع" : "Requires return invoice to reverse")}
                      {protectionLevel === "strong" && (language === "ar" ? "غير قابلة للحذف نهائياً — مرتجع فقط" : "Permanent — return only")}
                    </p>
                  </div>

                  {/* WhatsApp Auto-Send Toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-400 text-sm">
                        {language === "ar" ? "إرسال الفاتورة عبر WhatsApp" : "Send receipt via WhatsApp"}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAutoWhatsApp(!autoWhatsApp)}
                      className={`h-8 px-3 ${autoWhatsApp ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}`}
                      data-testid="whatsapp-toggle-btn"
                    >
                      {autoWhatsApp 
                        ? (language === "ar" ? "✓ مفعّل" : "✓ On")
                        : (language === "ar" ? "معطّل" : "Off")}
                    </Button>
                  </div>
                  
                  {autoWhatsApp && !selectedCustomer && !(customerInputMode === "manual" && manualCustomerPhone) && (
                    <p className="text-xs text-amber-400 text-center">
                      {language === "ar" 
                        ? "💡 اختر زبون برقم هاتف لإرسال الفاتورة تلقائياً" 
                        : "💡 Select a customer with phone number to auto-send receipt"}
                    </p>
                  )}

                  {/* Checkout Button */}
                  <Button
                    onClick={handleCheckout}
                    disabled={loading || cart.length === 0}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg font-bold"
                    data-testid="checkout-btn"
                  >
                    {loading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 me-2" />
                        {language === "ar" ? "إتمام البيع" : "Complete Sale"}
                      </>
                    )}
                  </Button>
                  
                  {/* Hold Invoice Button */}
                  <Button
                    onClick={handleHoldInvoice}
                    disabled={cart.length === 0}
                    variant="outline"
                    className="w-full border-amber-500/40 text-amber-400 hover:bg-amber-500/10 py-4"
                    data-testid="hold-invoice-btn"
                  >
                    ⏸ {language === "ar" ? "تعليق الفاتورة" : "Hold Invoice"}
                  </Button>

                  {/* Held Invoices List Button (inside POS — opens modal) */}
                  <Button
                    onClick={openHeldInvoicesModal}
                    variant="outline"
                    className="w-full border-purple-500/40 text-purple-400 hover:bg-purple-500/10 py-4"
                    data-testid="held-invoices-list-btn"
                  >
                    📂 {language === "ar" ? "الفواتير المعلقة" : "Held Invoices"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Held Invoices Modal — منبثقة داخل POS */}
      {showHeldInvoicesModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowHeldInvoicesModal(false)}
          data-testid="held-invoices-modal"
        >
          <div
            className="bg-slate-900 border border-purple-500/30 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-gradient-to-r from-purple-600/20 to-fuchsia-600/20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📂</span>
                <h2 className="text-xl font-bold text-white">الفواتير المعلقة</h2>
                {heldInvoicesList.length > 0 && (
                  <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {heldInvoicesList.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowHeldInvoicesModal(false)}
                className="text-slate-400 hover:text-white text-2xl leading-none"
                aria-label="إغلاق"
                data-testid="close-held-modal-btn"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4">
              {heldInvoicesLoading ? (
                <div className="flex items-center justify-center py-12 text-slate-400">
                  <span className="animate-spin mr-2">⏳</span> جاري التحميل...
                </div>
              ) : heldInvoicesList.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <p className="text-lg mb-2">لا توجد فواتير معلقة حالياً</p>
                  <p className="text-sm">الفواتير التي تُعلّقها ستظهر هنا</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {heldInvoicesList.map((h) => {
                    const itemsCount = (h.items || []).length;
                    const total = h.total ?? (h.items || []).reduce(
                      (s, it) => s + (it.price || 0) * (it.quantity || 1),
                      0
                    );
                    const customerName = h.customer_name ||
                      customers.find((c) => c.id === h.customer_id)?.name ||
                      "بدون عميل";
                    const heldAt = h.held_at || h.created_at;
                    const date = heldAt
                      ? new Date(heldAt).toLocaleString("ar-IQ", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : "";
                    const noteText = h.notes || h.note;
                    return (
                      <div
                        key={h.id}
                        className="bg-slate-800 hover:bg-slate-700/70 border border-slate-700 rounded-xl p-3 flex items-center gap-3 transition"
                        data-testid={`held-row-${h.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-white truncate">{customerName}</span>
                            {noteText && (
                              <span className="text-xs text-amber-400 truncate">📝 {noteText}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 flex items-center gap-3 flex-wrap">
                            <span>🛒 {itemsCount} مادة</span>
                            <span>💰 {Number(total).toLocaleString("ar-IQ")} د.ع</span>
                            {date && <span>🕓 {date}</span>}
                          </div>
                        </div>
                        <Button
                          onClick={() => resumeHeldInvoice(h)}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`resume-held-btn-${h.id}`}
                        >
                          استكمال
                        </Button>
                        <Button
                          onClick={() => deleteHeldInvoice(h.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid={`delete-held-btn-${h.id}`}
                        >
                          🗑
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-700 bg-slate-900/80 flex justify-end">
              <Button
                onClick={() => setShowHeldInvoicesModal(false)}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                إغلاق
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Dialog */}
      <ReceiptDialog
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        data={completedSale}
        type={completedSale?.payment_type === "credit" ? "sale_credit" : "sale_cash"}
        language={language}
      />

      {/* Invoice Viewer (تصفّح الفواتير من رأس POS) */}
      <ReceiptDialog
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        data={viewedSale}
        type={viewedSale?.payment_type === "credit" ? "sale_credit" : "sale_cash"}
        language={language}
        allowReturn={true}
        onReturned={async () => {
          fetchData();
          try {
            const res = await axios.get(`${API}/sales`);
            const list = Array.isArray(res.data) ? res.data : [];
            setSalesHistory(list);
          } catch (e) { /* ignore */ }
        }}
        navigation={(() => {
          if (!viewedSale || !salesHistory || salesHistory.length === 0) return null;
          const idx = salesHistory.findIndex((s) => s.id === viewedSale.id);
          if (idx < 0) return null;
          const goto = (i) => {
            if (i >= 0 && i < salesHistory.length) setViewedSale(salesHistory[i]);
          };
          return {
            currentIndex: idx,
            total: salesHistory.length,
            onFirst: () => goto(0),
            onPrev: () => goto(idx - 1),
            onNext: () => goto(idx + 1),
            onLast: () => goto(salesHistory.length - 1),
          };
        })()}
      />

      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScan={handleBarcodeScan}
        language={language}
      />
    </div>
  );
}
